import {
  DEFAULT_BETWEEN_ROW_SPACING,
  DEFAULT_IN_ROW_SPACING,
  sectionTypes,
  UNKNOWN_CROP,
} from '../constants/fields';
import { getFirstQueryResult } from './graphql';
import { getSectionMeta } from './meta';
import { gzipIfNeeded } from './gzip';
import { generateDbId } from './utils';
import {
  addPointsAndRows,
  uploadGeodataInputsMap,
} from './points-and-rows/addPointsAndRows';

/*
Returns true if a section update requires re-generating crop points and rows.
*/
const shouldUploadGeodata = (original, update, type) => {
  // Only upload geodata if there is a field feature.
  if (!original.fieldFeature && !update.fieldFeature) return false;
  const uploadGeodataInputs = uploadGeodataInputsMap[type];
  return Object.keys(update).some(
    (k) => uploadGeodataInputs.includes(k) && original[k] !== update[k],
  );
};

// Returns a copy of original with update applied. Will generate points and rows
// if necessary and include the updated crop values.
export const mergeSections = (original, update, dataMaps) => {
  const merged = { ...original, ...update };
  return shouldUploadGeodata(original, update, merged.type)
    ? {
        section: addPointsAndRows(merged, dataMaps),
        uploadGeodata: true,
      }
    : { section: merged, uploadGeodata: false };
};

export const newDefaultSection = ({ crop = UNKNOWN_CROP, ...values }) => ({
  id: generateDbId('SECTION'),
  name: 'New Field',
  type: sectionTypes.GRID,
  crop,
  inRowSpacing: crop?.inRowSpacing || DEFAULT_IN_ROW_SPACING,
  betweenRowSpacing: crop?.betweenRowSpacing || DEFAULT_BETWEEN_ROW_SPACING,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  rowOrderIsReversed: false,
  orderWithinRowsIsReversed: false,
  rows: null,
  points: null,
  fieldFeature: null,
  rowOverrides: [],
  ...values,
});

export const newImportedSection = ({ name, crop, ...properties }) => ({
  id: generateDbId('SECTION'),
  name,
  type: sectionTypes.IMPORTED,
  crop,
  inRowSpacing: crop.inRowSpacing || DEFAULT_IN_ROW_SPACING,
  // no betweenRowSpacing
  // no rotation
  rows: [],
  points: null,
  fieldFeature: null,
  rowOverrides: [],
  ...properties,
});

/* Write and read section geodata to and from S3 */

export const writeSectionS3Geodata = async (
  section,
  uploadGeodata,
  getSignedPutUrl,
) => {
  // Only send request to S3 if the points or rows have been updated
  if (!uploadGeodata) {
    // Return undefined since the S3 version id was not updated
    return Promise.resolve();
  }

  const sectionGeodata = {
    points: section.points,
    rows: section.rows,
  };

  // Get presigned PUT URL
  const { data } = await getSignedPutUrl({
    id: section.id,
    farmId: section.farmId,
  });

  const json = JSON.stringify(sectionGeodata);
  const { body, headers } = await gzipIfNeeded(json);

  const response = await fetch(getFirstQueryResult(data), {
    method: 'PUT',
    body,
    headers: {
      'content-type': 'application/json',
      // Set Type=Section tag to apply lifecycle rules to section objects
      'x-amz-tagging': 'Type=Section',
      ...headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Cannot save geography data: ${response.statusText}`);
  }

  // Return S3 object version id from header
  return response.headers.get('x-amz-version-id');
};

const readSectionS3Geodata = (section, getSignedUrl = null) => {
  // Get points and rows from S3
  return fetch(section.signedUrl, {
    headers: {
      accept: 'application/json',
    },
  }).then((response) => {
    if (response.ok) return response.json();
    if ([403, 404].includes(response.status) && getSignedUrl != null) {
      // Get presigned GET URL without version ID
      return getSignedUrl({ id: section.id, includeVersionId: false }).then(
        ({ data }) => {
          const retrySection = {
            ...section,
            signedUrl: getFirstQueryResult(data).signedUrl,
          };
          return readSectionS3Geodata(retrySection).then(
            ({ warning, ...result }) => ({
              ...result,
              warning:
                warning ||
                `Section data may be out of sync for ${section.name}`,
            }),
          );
        },
      );
    }
    // Key has no data, allow the section to be loaded to recover from this state
    if ([403, 404].includes(response.status)) {
      return { warning: `No data for section ${section.name}` };
    }
    throw new Error(response.statusText);
  });
};

export const parseRemoteSections = (remoteSections, getSignedUrl) => {
  return remoteSections.map((section) => {
    // Read points and rows from S3
    return (
      readSectionS3Geodata(section, getSignedUrl)
        // Parse the section to store in the frontend
        .then(({ points, rows, warning }) => {
          const parsedSection = {
            ...section,
            points: points || { features: [] },
            rows: rows || { features: [] },
            rowOverrides: JSON.parse(section.rowOverrides || '[]'),
            warning,
          };
          const { rowCount } = getSectionMeta(parsedSection);
          const inputRows =
            parsedSection.type === sectionTypes.LINE
              ? { inputRowCount: rowCount }
              : {};
          return {
            ...parsedSection,
            ...inputRows,
          };
        })
        // Catch any errors and return with the name of the section
        .catch((error) => ({ name: section.name, error }))
    );
  });
};

/* Row Ids */
const ROW_ID_DELIM = ':';
const ROW_ID_PREFIX = 'ROW';

// To ROW:<rowSection>:<rowIndex>
export const getRowId = (sectionId, rowIndex) =>
  [ROW_ID_PREFIX, sectionId, rowIndex].join(ROW_ID_DELIM);

// ROW:<rowSection>:<rowIndex> to { rowSection, rowIdx }
export const parseRowId = (rowId) => {
  if (!rowId) return {};
  const [rowString, rowSection, rowIndexStr] = rowId.split(ROW_ID_DELIM);
  return rowString === ROW_ID_PREFIX
    ? { rowSection, rowIdx: parseInt(rowIndexStr, 10) }
    : {};
};
